import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import CardDropdown from 'components/common/CardDropdown';
import SoftBadge from 'components/common/SoftBadge';
import TableHeader from 'components/common/TableHeader';
import LoadingWrapper from 'components/loading/LoadingWrapper';
import ConfirmationModal from 'components/modal/ConfirmationModal';
import useDelete from 'hooks/useDelete';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Card, Dropdown } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getBlogs } from 'services/content';
import serviceUrl from '../../../services/serviceUrlList';
import BlogTableFilter from './module/BlogTableFilter';

const Blog = () => {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const [totalCount, setTotalCount] = useState(0);

  const [confirmText, setConfirmText] = useState({
    title: '',
    description: '',
    type: ''
  });
  let [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!searchParams.has('offset')) {
      searchParams.set('offset', 0);
    }
    if (!searchParams.has('limit')) {
      searchParams.set('limit', 10);
    }

    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const { data, isLoading, refetch } = useQuery(
    ['getBlogs', searchParams.toString()],
    () => getBlogs(searchParams.toString()),
    {
      keepPreviousData: false,
      staleTime: Infinity
    }
  );
  useEffect(() => {
    refetch();
  }, []);

  const [deleteResponse, deleteRequest] = useDelete({
    url: serviceUrl.content.blog.detail + selectedRowId
  });

  const handleDeleteItem = id => {
    setSelectedRowId(id);
    setConfirmText({
      title: 'Delete Blog',
      description: 'Are you sure? Selected blog will be deleted!',
      type: 'delete'
    });
    setShowConfirm(true);
  };

  useEffect(() => {
    if (deleteResponse.data === null && deleteResponse.error === null) return;
    refetch();
    setShowConfirm(false);
    toast.success(`Blog deleted`, {
      theme: 'colored'
    });
    setSelectedRowId(null);
  }, [deleteResponse]);

  useEffect(() => {
    if (!data?.data.count) return;
    setTotalCount(data?.data.count);
  }, [data?.data.count]);

  const columns = [
    {
      accessor: 'name',
      Header: 'Title',
      headerProps: { className: 'text-start' },
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { slug, name } = rowData.row.original;
        return (
          <Link to={`/cms/blog-update/${slug}`} state={searchParams.toString()}>
            {name}
          </Link>
        );
      }
    },
    {
      accessor: 'categories',
      Header: 'Category',
      headerProps: { className: 'pe-1 text-start' },
      cellProps: {
        className: 'py-2 text-truncate',
        style: { maxWidth: 200 }
      },
      Cell: rowData => {
        const { category } = rowData.row.original;
        return <>{category?.name}</>;
      }
    },
    {
      accessor: 'is_featured',
      Header: 'Featured',
      cellProps: { className: 'text-center' },
      Cell: rowData => {
        const { is_featured } = rowData.row.original;
        if (is_featured) {
          return (
            <SoftBadge pill bg="success" className="d-inline-block">
              Yes
            </SoftBadge>
          );
        }
        return (
          <SoftBadge pill bg="danger" className="d-inline-block">
            No
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'author',
      Header: 'Author',
      headerProps: { className: 'text-start' },
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'publish_date',
      Header: 'Publish Date',
      headerProps: { className: 'text-start' },
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { publish_date } = rowData.row.original;
        if (publish_date) {
          return moment(publish_date).format('DD.MM.YYYY HH:mm');
        }
      }
    },
    {
      accessor: 'is_active',
      Header: 'Status',
      cellProps: { className: 'text-center' },
      Cell: rowData => {
        const { is_active } = rowData.row.original;
        if (is_active) {
          return (
            <SoftBadge pill bg="success" className="d-block">
              Active
            </SoftBadge>
          );
        }
        return (
          <SoftBadge pill bg="danger" className="d-block">
            Passive
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'none',
      Header: 'Actions',
      disableSortBy: true,
      headerProps: { className: 'text-end' },
      cellProps: {
        className: 'text-end'
      },
      Cell: rowData => {
        const { slug } = rowData.row.original;
        return (
          <CardDropdown iconClassName="fs--1">
            <div className="py-2">
              <Dropdown.Item
                onClick={() =>
                  navigate(`/cms/blog-update/${slug}`, {
                    state: searchParams.toString()
                  })
                }
              >
                Edit
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDeleteItem(slug)}>
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      }
    }
  ];

  const handleFilter = (key, value) => {
    if (value === null || value === undefined) {
      setSearchParams(prev => {
        const search = new URLSearchParams(prev);
        search.set('offset', 0);
        search.delete(key);
        return search.toString();
      });
      return;
    }

    setSearchParams(prev => {
      const search = new URLSearchParams(prev);
      search.set('offset', 0);
      search.set(key, value);
      return search.toString();
    });
  };

  const handleNew = () => {
    navigate('/cms/blog-add');
  };

  return (
    <div className="p-2">
      <LoadingWrapper loading={isLoading}>
        <AdvanceTableWrapper
          columns={columns}
          data={data?.data.results || []}
          selection
          sortable
          pagination
          perPage={10}
          totalCount={totalCount}
        >
          <Card className="mb-3">
            <Card.Header>
              <TableHeader
                title="Blog"
                handleRefetch={refetch}
                table
                handleNew={handleNew}
                bulkTable={'Blog'}
              />
              <BlogTableFilter handleFilter={handleFilter} />
            </Card.Header>
            <Card.Body className="p-0">
              <AdvanceTable
                table
                headerClassName="bg-200 text-900 text-nowrap align-middle text-center"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  className: 'fs--1 mb-0'
                }}
                rowClassName="align-middle"
              />
            </Card.Body>
            <Card.Footer>
              <AdvanceTablePagination
                table
                urlPagination
                initialPage={searchParams.get('offset') / 10}
              />
            </Card.Footer>
          </Card>
        </AdvanceTableWrapper>
      </LoadingWrapper>

      <ConfirmationModal
        showConfirm={showConfirm}
        setShowConfirm={setShowConfirm}
        title={confirmText.title}
        description={confirmText.description}
        request={deleteRequest}
        type={confirmText.type}
      />
    </div>
  );
};

export default Blog;
